import React, { useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useColumns } from './dated-columns';
import { DatedFields } from './dated-fields';
import {
  DatedObject,
  EvidenceProps,
  TableSort,
  useEventCallback,
} from '@eas/common-web';
import { DatedEvidenceProps } from './dated-types';
import { useNotedEvidence } from '../noted-evidence/noted-evidence';

export function useDatedEvidence<OBJECT extends DatedObject>(
  options: DatedEvidenceProps<OBJECT>
): EvidenceProps<OBJECT> {
  const columns = useColumns({
    columns: options.tableProps?.columns,
    showColumn: options.tableProps?.showColumn,
    columnLabelMapper: options.tableProps?.columnLabelMapper,
    columnWrapper: options.tableProps?.columnWrapper,
  });

  const defaultSorts: TableSort[] = useMemo(
    () =>
      options.tableProps?.defaultSorts ?? [
        { field: 'created', datakey: 'created', order: 'DESC', type: 'FIELD' },
      ],
    [options.tableProps?.defaultSorts]
  );

  const GeneralFieldsComponent = useMemo(
    () =>
      function Fields() {
        return (
          <DatedFields
            FieldsComponent={options.detailProps?.GeneralFieldsComponent}
          />
        );
      },
    [options.detailProps?.GeneralFieldsComponent]
  );

  const initNewItem = useEventCallback(() => {
    const obj: OBJECT = {
      id: uuidv4(),
    } as any;

    if (options.detailProps?.initNewItem !== undefined) {
      return {
        ...obj,
        ...options.detailProps?.initNewItem(),
      };
    } else {
      return obj;
    }
  });

  return useNotedEvidence({
    ...options,
    tableProps: { ...options.tableProps, columns, defaultSorts },
    detailProps: {
      ...options.detailProps,
      initNewItem,
      GeneralFieldsComponent: options.detailProps.hideGeneralFields
        ? undefined
        : GeneralFieldsComponent,
      toolbarProps: options.detailProps?.toolbarProps,
    },
  });
}
